// src/components/RightDrawer.tsx

import * as React from 'react';
import {
  Box,
  SwipeableDrawer,
  Typography,
  Divider,
  TextField,
  Button,
  IconButton,
  Stack,
  Autocomplete,
  Chip,
  useTheme,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckIcon from '@mui/icons-material/Check';
import { useLocation, useNavigate } from 'react-router-dom';
// ✅ Import Encoder/Decoder
import { encodeId, decodeId } from '../utils/idEncoder';

// --- Types ---
interface FilterOption {
  [key: string]: any;
  description?: string;
  dealerCompanyName?: string;
}

type RightDrawerProps = {
  open: boolean;
  onClose: () => void;
  fuelTypeData?: any[];
  TransmissionTypeData?: any[];
  bodyTypesData?: any[];
  brandData?: any[];
  dealerData?: any[];
};

type SelectedFilters = {
  vehicleFuelTypeIds: string[];
  vehicleTransmissionTypeIds: string[];
  vehicleBodyTypeIds: string[];
  vehicleBrandIds: string[];
  dealerIds: string[];
  minAskingPrice: string;
  maxAskingPrice: string;
  minManufacturingYear: string;
  maxManufacturingYear: string;
  minMileage: string;
  maxMileage: string;
};

// Define keys that should be encrypted
const ENCRYPTED_KEYS = [
    'vehicleBrandIds',
    'vehicleBodyTypeIds',
    'vehicleFuelTypeIds',
    'vehicleTransmissionTypeIds',
    'dealerIds'
];

const RightDrawer: React.FC<RightDrawerProps> = ({
  open,
  onClose,
  fuelTypeData = [],
  TransmissionTypeData = [],
  bodyTypesData = [],
  brandData = [],
  dealerData = [],
}) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // --- State ---
  const [selectedFilters, setSelectedFilters] = React.useState<SelectedFilters>({
    vehicleFuelTypeIds: [],
    vehicleTransmissionTypeIds: [],
    vehicleBodyTypeIds: [],
    vehicleBrandIds: [],
    dealerIds: [],
    minAskingPrice: '',
    maxAskingPrice: '',
    minManufacturingYear: '',
    maxManufacturingYear: '',
    minMileage: '',
    maxMileage: '',
  });

  // Initialize state when drawer opens
  React.useEffect(() => {
    if (open) {
      initializeFiltersFromURL();
    }
  }, [open]);

  const initializeFiltersFromURL = () => {
    const searchParams = new URLSearchParams(location.search);
    
    // Helper to get and potentially decode array params
    const getArrayParam = (key: string) => {
        const raw = searchParams.get(key);
        if (!raw) return [];
        const parts = raw.split(',').map((v) => v.trim()).filter(Boolean);

        // ✅ DECODE if it's an encrypted key
        if (ENCRYPTED_KEYS.includes(key)) {
            return parts.map(hash => decodeId(hash)?.toString() || '').filter(Boolean);
        }
        return parts;
    };

    setSelectedFilters({
      vehicleFuelTypeIds: getArrayParam('vehicleFuelTypeIds'),
      vehicleTransmissionTypeIds: getArrayParam('vehicleTransmissionTypeIds'),
      vehicleBodyTypeIds: getArrayParam('vehicleBodyTypeIds'),
      vehicleBrandIds: getArrayParam('vehicleBrandIds'),
      dealerIds: getArrayParam('dealerIds'),
      minAskingPrice: searchParams.get('minAskingPrice') || '',
      maxAskingPrice: searchParams.get('maxAskingPrice') || '',
      minManufacturingYear: searchParams.get('minManufacturingYear') || '',
      maxManufacturingYear: searchParams.get('maxManufacturingYear') || '',
      minMileage: searchParams.get('minMileage') || '',
      maxMileage: searchParams.get('maxMileage') || '',
    });
  };

  const applyFilters = () => {
    const currentParams = new URLSearchParams(location.search);

    Object.entries(selectedFilters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        if (value.length > 0) {
           // ✅ ENCODE if it's an encrypted key before setting URL
           if (ENCRYPTED_KEYS.includes(key)) {
               const encodedValues = value.map(id => encodeId(id));
               currentParams.set(key, encodedValues.join(','));
           } else {
               currentParams.set(key, value.join(','));
           }
        } else {
          currentParams.delete(key);
        }
      } else if (value !== '' && value !== null) {
        currentParams.set(key, String(value));
      } else {
        currentParams.delete(key);
      }
    });

    currentParams.delete('page');
    navigate(`/used-vehicles?${currentParams.toString()}`);
    onClose();
  };

  const resetFilters = () => {
    setSelectedFilters({
      vehicleFuelTypeIds: [],
      vehicleTransmissionTypeIds: [],
      vehicleBodyTypeIds: [],
      dealerIds: [],
      vehicleBrandIds: [],
      minAskingPrice: '',
      maxAskingPrice: '',
      minManufacturingYear: '',
      maxManufacturingYear: '',
      minMileage: '',
      maxMileage: '',
    });
    navigate('/used-vehicles');
    onClose();
  };

  // --- Dynamic Option Generators for Ranges ---
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 25 }, (_, i) => currentYear - i);
  const priceOptions = Array.from({ length: 60 }, (_, i) => (i + 1) * 5000);
  const mileageOptions = Array.from({ length: 30 }, (_, i) => (i + 1) * 10000);

  // --- Render Helpers ---
  const FilterSectionLabel = ({ children }: { children: React.ReactNode }) => (
    <Typography 
      variant="overline" 
      fontWeight={800} 
      color="text.secondary" 
      sx={{ mb: 1.5, display: 'block', letterSpacing: 1 }}
    >
      {children}
    </Typography>
  );

  const inputStyles = {
    '& .MuiOutlinedInput-root': { 
      borderRadius: 3, 
      bgcolor: '#f5f7fa',
      '& fieldset': { borderColor: 'transparent' },
      '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.1)' },
      '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: '2px' },
    }
  };

  const renderAutocomplete = (
    label: string,
    key: keyof SelectedFilters,
    data: any[],
    idField: string,
    labelField: string,
    imageField?: string
  ) => {
    const selectedValues = data.filter((item) =>
      (selectedFilters[key] as string[]).includes(String(item[idField]))
    );

    return (
      <Box mb={4}>
        <FilterSectionLabel>{label}</FilterSectionLabel>
        <Autocomplete
          multiple
          disableCloseOnSelect
          limitTags={3}
          options={data}
          value={selectedValues}
          getOptionLabel={(option) => option[labelField]}
          isOptionEqualToValue={(option, value) => String(option[idField]) === String(value[idField])}
          onChange={(_, newValue) =>
            setSelectedFilters({
              ...selectedFilters,
              [key]: newValue.map((val) => String(val[idField])),
            })
          }
          renderInput={(params) => (
            <TextField 
              {...params} 
              placeholder={selectedValues.length === 0 ? `Select ${label.toLowerCase()}...` : ""}
              size="small" 
              sx={inputStyles}
            />
          )}
          renderOption={(props, option, { selected }) => (
            <li {...props} style={{ padding: '8px 16px', borderRadius: '8px', margin: '0 8px 4px 8px' }}>
              <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={1.5}>
                  {imageField && option[imageField] && (
                    <Box 
                      component="img" 
                      src={option[imageField]} 
                      sx={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'contain', bgcolor: '#fff', border: '1px solid #eee' }}
                    />
                  )}
                  <Typography variant="body2" fontWeight={selected ? 600 : 400}>
                    {option[labelField]}
                  </Typography>
                </Box>
                {selected && <CheckIcon fontSize="small" color="primary" />}
              </Box>
            </li>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={option[labelField]}
                size="small"
                {...getTagProps({ index })}
                sx={{ 
                  borderRadius: '16px', 
                  backgroundColor: 'primary.light', 
                  color: 'primary.dark',
                  fontWeight: 600,
                  border: '1px solid',
                  borderColor: 'primary.main',
                  '& .MuiChip-deleteIcon': { color: 'primary.dark' }
                }}
              />
            ))
          }
        />
      </Box>
    );
  };

  // NEW: Unified Dual Select for Ranges (replaces sliders and basic dropdowns)
  const renderDualSelectRange = (
    label: string,
    minKey: keyof SelectedFilters,
    maxKey: keyof SelectedFilters,
    options: number[],
    formatLabel: (val: number) => string
  ) => {
    const minVal = selectedFilters[minKey] ? Number(selectedFilters[minKey]) : '';
    const maxVal = selectedFilters[maxKey] ? Number(selectedFilters[maxKey]) : '';

    return (
      <Box mb={4}>
        <FilterSectionLabel>{label}</FilterSectionLabel>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            select
            fullWidth
            size="small"
            label="Lowest"
            value={minVal}
            onChange={(e) => setSelectedFilters({ ...selectedFilters, [minKey]: e.target.value.toString() })}
            sx={inputStyles}
          >
            <MenuItem value=""><em>Any</em></MenuItem>
            {options.map(opt => (
              <MenuItem key={`min-${opt}`} value={opt} disabled={maxVal !== '' && opt > (maxVal as number)}>
                {formatLabel(opt)}
              </MenuItem>
            ))}
          </TextField>
          
          <Typography variant="body2" color="text.secondary" fontWeight={500}>to</Typography>
          
          <TextField
            select
            fullWidth
            size="small"
            label="Highest"
            value={maxVal}
            onChange={(e) => setSelectedFilters({ ...selectedFilters, [maxKey]: e.target.value.toString() })}
            sx={inputStyles}
          >
            <MenuItem value=""><em>Any</em></MenuItem>
            {options.map(opt => (
              <MenuItem key={`max-${opt}`} value={opt} disabled={minVal !== '' && opt < (minVal as number)}>
                {formatLabel(opt)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Box>
    );
  };

  // Calculate active filters count for the badge
  const activeFiltersCount = Object.values(selectedFilters).filter(v => (Array.isArray(v) ? v.length > 0 : v !== '')).length;

  return (
    <SwipeableDrawer 
      anchor="right" 
      open={open} 
      onClose={onClose} 
      onOpen={initializeFiltersFromURL}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 420 }, borderRadius: { xs: 0, sm: '16px 0 0 16px' } } 
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
        
        {/* --- Header --- */}
        <Box
          px={3}
          py={2.5}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: '#fff',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography variant="h6" fontWeight="800">
              Advanced Filters
            </Typography>
            {activeFiltersCount > 0 && (
              <Box 
                sx={{ 
                  bgcolor: 'primary.main', 
                  color: 'primary.contrastText', 
                  borderRadius: '12px', 
                  px: 1.2, 
                  py: 0.2, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                {activeFiltersCount}
              </Box>
            )}
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ bgcolor: '#f0f2f5', '&:hover': { bgcolor: '#e4e6eb' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* --- Scrollable Body --- */}
        <Box 
          sx={{ 
            flexGrow: 1, 
            overflowY: 'auto', 
            px: 3,
            py: 4,
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#ddd', borderRadius: '3px' },
          }}
        >
          {/* Main Filters */}
          {renderAutocomplete('Brand', 'vehicleBrandIds', brandData, 'vehicleBrandId', 'description')}
          {renderAutocomplete('Dealer', 'dealerIds', dealerData, 'dealerCompanyId', 'dealerCompanyName', 'imageUrl')}
          
          <Divider sx={{ my: 4, borderStyle: 'dashed', borderColor: '#ccc' }} />
          
          {/* Replaced Slider and simple select with the new Dual Select Range */}
          {renderDualSelectRange('Price Range', 'minAskingPrice', 'maxAskingPrice', priceOptions, (v) => `$${v.toLocaleString()}`)}
          {renderDualSelectRange('Manufacturing Year', 'minManufacturingYear', 'maxManufacturingYear', yearOptions, (v) => v.toString())}
          
          <Divider sx={{ my: 4, borderStyle: 'dashed', borderColor: '#ccc' }} />

          {renderAutocomplete('Body Type', 'vehicleBodyTypeIds', bodyTypesData, 'vehicleBodyTypeId', 'description')}
          {renderAutocomplete('Fuel Type', 'vehicleFuelTypeIds', fuelTypeData, 'vehicleFuelTypeId', 'description')}
          {renderAutocomplete('Transmission', 'vehicleTransmissionTypeIds', TransmissionTypeData, 'vehicleTransmissionTypeId', 'description')}
          
          {/* Replaced old range select with new Dual Select Range */}
          {renderDualSelectRange('Mileage', 'minMileage', 'maxMileage', mileageOptions, (v) => `${v.toLocaleString()} km`)}
        </Box>

        {/* --- Footer Actions --- */}
        <Box
          p={3}
          sx={{
            bgcolor: '#fff',
            boxShadow: '0 -10px 30px rgba(0,0,0,0.06)',
            position: 'relative',
            zIndex: 10,
          }}
        >
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              color="inherit" 
              fullWidth
              startIcon={<RestartAltIcon />}
              onClick={resetFilters}
              sx={{ 
                borderRadius: 3, 
                py: 1.2,
                borderColor: '#ccc', 
                color: 'text.primary',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { bgcolor: '#f5f5f5', borderColor: '#aaa' }
              }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={applyFilters}
              sx={{ 
                borderRadius: 3, 
                py: 1.2,
                fontWeight: 700,
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(255, 161, 0, 0.3)',
                color: '#000', 
              }}
            >
              Show {activeFiltersCount > 0 ? 'Results' : 'All Vehicles'}
            </Button>
          </Stack>
        </Box>

      </Box>
    </SwipeableDrawer>
  );
};

export default RightDrawer;